import { codePays } from './enlevement.js';
/**
 * Indicatifs des pays où Mosaik expédie, et longueur du numéro national.
 *
 * `national` compte les chiffres APRÈS le zéro de service : neuf pour la
 * France (`06 12 34 56 78`), neuf pour la Belgique mobile comme fixe à deux
 * chiffres près — d'où un intervalle plutôt qu'un nombre, partout où le plan
 * de numérotation n'est pas à longueur fixe.
 *
 * La liste suit celle de `codePays`, à laquelle ce module délègue la
 * reconnaissance : un pays qu'elle nomme mais qui manque ici rend le numéro
 * invalide plutôt qu'approximatif.
 */
const INDICATIFS = {
    FR: { code: '33', national: [9, 9] },
    BE: { code: '32', national: [8, 9] },
    DE: { code: '49', national: [6, 11] },
    IT: { code: '39', national: [9, 10] },
    ES: { code: '34', national: [9, 9] },
    CH: { code: '41', national: [9, 9] },
    LU: { code: '352', national: [8, 9] },
    NL: { code: '31', national: [9, 9] },
    GB: { code: '44', national: [9, 10] },
    PT: { code: '351', national: [9, 9] },
    AT: { code: '43', national: [7, 12] },
    PL: { code: '48', national: [9, 9] },
    SE: { code: '46', national: [7, 9] },
    NO: { code: '47', national: [8, 8] },
    DK: { code: '45', national: [8, 8] },
    FI: { code: '358', national: [6, 10] },
    IE: { code: '353', national: [7, 9] },
    CZ: { code: '420', national: [9, 9] },
    GR: { code: '30', national: [10, 10] },
    RO: { code: '40', national: [9, 9] },
    HU: { code: '36', national: [8, 9] },
};
/** Tous les indicatifs connus, du plus long au plus court : `352` avant `35`. */
const CODES_CONNUS = Object.values(INDICATIFS)
    .map((i) => i.code)
    .sort((a, b) => b.length - a.length);
/**
 * Longueur totale acceptable d'un numéro E.164, indicatif compris.
 *
 * La norme plafonne à quinze chiffres. Le plancher de huit écarte les numéros
 * tronqués — « 06 12 34 » — que la saisie manuelle produit régulièrement, et
 * qu'aucun opérateur ne saurait joindre.
 */
const LONGUEUR_MIN = 8;
const LONGUEUR_MAX = 15;
/**
 * Ce qui se retire sans rien changer au numéro : espaces sous toutes leurs
 * formes, points, tirets, barres, parenthèses. Le `(0)` des écritures mixtes
 * — `+33 (0)6 …` — est traité à part, car il faut retirer le zéro AUSSI.
 */
function nettoyer(brut) {
    return brut
        .replace(/\((\s*0\s*)\)/g, '')
        .replace(/[\s  .\-/_()]/g, '')
        .trim();
}
/**
 * Met un numéro au format international.
 *
 * `pays` est celui de l'adresse de livraison, en clair ou en code ISO : il ne
 * sert QUE pour les numéros en forme nationale, les autres portant déjà leur
 * indicatif. Sans pays reconnu, un numéro national est rendu invalide — voir
 * l'en-tête du module.
 */
export function normaliserTelephone(brut, pays) {
    const valeur = String(brut ?? '').trim();
    if (!valeur)
        return { brut: null, e164: null, valide: false, raison: null };
    const rate = (raison) => ({ brut: valeur, e164: null, valide: false, raison });
    // `00` est le préfixe international composé ; il vaut `+`, et se rencontre
    // aussi souvent que lui dans les saisies.
    const nettoye = nettoyer(valeur).replace(/^00/, '+');
    if (/[^+0-9]/.test(nettoye)) {
        return rate('Le numéro contient des caractères qui ne sont ni des chiffres ni un « + ».');
    }
    // Un `+` ailleurs qu'en tête n'est pas un numéro, c'est deux numéros collés.
    // La recherche part du caractère 1 : celui de tête est légitime, et le
    // chercher depuis 0 ne trouvait jamais que lui — le contrôle ne servait rien.
    if (nettoye.indexOf('+', 1) !== -1)
        return rate('Le numéro semble en contenir deux.');
    if (nettoye.startsWith('+')) {
        const chiffres = nettoye.slice(1);
        if (chiffres.length < LONGUEUR_MIN)
            return rate('Le numéro international est trop court.');
        if (chiffres.length > LONGUEUR_MAX)
            return rate('Le numéro international est trop long.');
        if (!CODES_CONNUS.some((c) => chiffres.startsWith(c))) {
            return rate("L'indicatif pays n'est pas reconnu.");
        }
        return { brut: valeur, e164: `+${chiffres}`, valide: true, raison: null };
    }
    // Forme nationale : elle ne veut rien dire sans pays.
    const iso = codePays(pays);
    if (!iso) {
        return rate('Le numéro est au format national et le pays de livraison est inconnu : impossible de '
            + "deviner l'indicatif sans risquer de joindre quelqu'un d'autre.");
    }
    const plan = INDICATIFS[iso];
    if (!plan)
        return rate(`Le plan de numérotation du pays ${iso} n'est pas connu.`);
    if (!nettoye.startsWith('0')) {
        return rate('Un numéro national doit commencer par un zéro, ou porter son indicatif « + ».');
    }
    const national = nettoye.slice(1);
    const [min, max] = plan.national;
    if (national.length < min || national.length > max) {
        return rate(`Le numéro ne compte pas le bon nombre de chiffres pour ${iso} `
            + `(${national.length} au lieu de ${min === max ? min : `${min} à ${max}`}).`);
    }
    return { brut: valeur, e164: `+${plan.code}${national}`, valide: true, raison: null };
}
/**
 * `true` quand il faut réclamer un numéro avant d'agir.
 *
 * **Absent compte autant qu'illisible**, et c'est un correctif. Les deux
 * étaient distingués : un numéro manquant laissait passer la demande, au motif
 * que le scénario saurait se rabattre sur le mail. Il ne le sait pas — la
 * commande 39384 est partie avec `telephone: null`, et la sollicitation n'a
 * joint personne.
 *
 * Il n'y a d'ailleurs pas deux situations : dans les deux cas on n'a pas de
 * numéro composable, et la seule différence est qu'on peut montrer le mauvais
 * quand il existe. C'est une nuance d'affichage, pas de règle.
 */
export function telephoneAFournir(t) {
    return !t.valide;
}
//# sourceMappingURL=telephone.js.map